(ns dashboard.entity.document.diff
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            ["react-string-diff" :as react-diff]
            [clojure.string :as str]
            [dashboard.components.popup-menu :as popup-menu]
            [dashboard.entity.analysis.document-picker :as doc-picker]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.entity.document.attribution :as attribution]
            [dashboard.entity.document.db :as doc.db]
            [dashboard.entity.document.diff-db :as diff.db]
            [dashboard.entity.document.evidence :as evidence]
            [dashboard.entity.document.js-events :as js.events]
            [dashboard.entity.document.pdf :as pdf]
            [dashboard.services.documents :as documents]
            [dashboard.ui :as ui]
            [re-frame.core :as rf]
            [reagent.core :as r]))

;; ----------------------------------------------------------------------------
;;  Helper Functions
;; ----------------------------------------------------------------------------

(defn paragraphs->map
  [doc-diff]
  (merge (->> doc-diff
              :document-1-data
              (mapv (juxt :id identity))
              (into {}))
         (->> doc-diff
              :document-2-data
              (mapv (juxt :id identity))
              (into {}))))

;; ----------------------------------------------------------------------------
;;  Components
;; ----------------------------------------------------------------------------

(defn document-viewer-layout
  "Main layout for the pdF viewer."
  [{:keys [loaded
           set-pdf-ref]}]
  [:div.document-viewer
   [:div.pdf-viewer-wrapper.is-flex-grow-1.is-flex-shrink-1
    {:ref set-pdf-ref}]
   (when-not loaded [:div.document-viewer--loader])])

(defn document-differences-menus
  "Displays floating menus for Evidence Selection and Evidence Creation."
  [{:keys [active-highlight
           attribution
           attribution-props
           document-2-id
           on-cancel-evidence
           on-save-evidence
           on-search-query
           saving
           search-query
           selected-constructs
           selected-text
           set-selected-constructs]}]
  [:<>
   ;; Analysis Search and Evidence Creation Popup
   (if search-query
     [:f> evidence/viewer-analysis-search
      {:attribution-props attribution-props
       :on-cancel-evidence on-cancel-evidence
       :on-save-evidence on-save-evidence
       :search-query search-query
       :saving saving
       :selected-constructs selected-constructs
       :set-selected-constructs set-selected-constructs}]
     [:div.no-search-placeholder])

   ;; Evidence Selection Popover
   (when (and (seq selected-text)
              document-2-id
              (seq active-highlight)
              attribution)
     [evidence/document-selection
      {:search-query search-query
       :on-search-query on-search-query
       :selected-text selected-text}
      attribution
      false])])

(defn differences-overlay
  "Displays the content diff overlay"
  [{:keys [active-diff
           active-highlight
           document-2-id
           doc-evidence
           filename
           filename-2
           selected-text]}]
  (let [[difference-style set-difference-style] (react/useState react-diff/DiffMethod.WordsWithSpace)]
    [:div.document-diff--diff-overlay
     {:id "document-diff--diff-overlay"
      :class (when active-diff
               (cond
                 (= (:similarity active-diff) 1)
                 :document-diff---no-change
                 (< (:similarity active-diff) 0.25)
                 :document-diff---no-match
                 :else
                 :document-diff---change))}
     [:div.document-diff--diff
      (if document-2-id
       (if active-highlight
         (let [construct-ids (->> doc-evidence
                                  (filterv (comp (partial = (:evidence-id active-highlight))
                                                 :evidence-id))
                                  first
                                  :construct-ids)]
           (when (and (seq construct-ids)
                      (not (seq selected-text)))
             [:div.level
              [:h6.title.is-6.my-2 "Select text from " filename-2]
              (str "Select text from the right-hard document for create evidence for "
                   (count construct-ids)
                   " selected analyses.")]))
         [:div.level
          [:h6.title.is-6.my-2 "Transfer Evidence from " filename " to " filename-2]
          [:div "To copy evidence, select evidence to duplicate from Document Evidence column."]])
       [:<>
        [:h6.title.is-6.my-2 "Compare a document to " filename]
        "To begin, select a document to compare content and evidence."])
    ; Differences
     (when active-diff
       (cond
         (= (:similarity active-diff) 1)
         [:div.level-left
          [:div.document-diff--legend-square.document-diff---no-change]
          [:b "No Changes"]]
         (< (:similarity active-diff) 0.25)
         [:div.level-left
          [:div.document-diff--legend-square.document-diff---no-match]
          [:b "No Matching Content Found"]]
         :else
         ;; See: https://www.npmjs.com/package/react-string-diff
         [:div.level.document-diff--diff-data.is-align-items-start
          [:div.document-diff--legend-square.document-diff---change]
          [:div.is-flex-grow-1.mr-3
           [:> react-diff/StringDiff
           {:oldValue (:doc-2-text active-diff)
            :newValue (:doc-1-text active-diff)
            :styles {:added {:backgroundColor "#dcffda"}
                     :removed {:backgroundColor "#ffd4d2"}}
            :method difference-style}]]
          [:button.button.is-small
           {:class (if (= difference-style react-diff/DiffMethod.WordsWithSpace)
                     :is-primary
                     :is-secondary)
            :on-click #(set-difference-style react-diff/DiffMethod.WordsWithSpace)}
           "Words"]
          [:button.button.is-small
           {:class (if (= difference-style react-diff/DiffMethod.Sentences)
                     :is-primary
                     :is-secondary)
            :on-click #(set-difference-style react-diff/DiffMethod.Sentences)}
           "Sentences"]]))]]))

(defn document-differences-layout
  [{:keys [active-diff
           active-highlight
           active-highlight2
           analysis-to-view
           attribution
           attribution-props
           set-active-highlight
           all-analysis
           all-documents
           document-2-id
           doc-evidence
           doc-2-evidence
           filename
           filename-2
           loaded
           loaded2
           on-cancel-evidence
           on-save-evidence
           on-search-query
           on-select-document-2
           on-scroll-to-evidence
           saving
           search-query
           selected-constructs
           selected-text
           set-active-highlight2
           set-analysis-to-view
           set-pdf-ref
           set-pdf-ref2
           set-selected-constructs
           url2]}]
  (let [[modal-instance set-modal-instance] (react/useState (gensym))]
   [:div.document-diff
   [:div.message-header.is-highlight
    "Comparing " filename
    [:button.button.delete.is-info
     {:on-click #(rf/dispatch [::ui/pop-modal])}]]

   [document-differences-menus
    {:active-highlight active-highlight
     :attribution attribution
     :attribution-props attribution-props
     :document-2-id document-2-id
     :on-cancel-evidence on-cancel-evidence
     :on-save-evidence on-save-evidence
     :on-search-query on-search-query
     :saving saving
     :search-query search-query
     :selected-constructs selected-constructs
     :selected-text selected-text
     :set-selected-constructs set-selected-constructs}]

   [:div.document-diff--content.level-left
    {}
    ; Original Document Evidence Panel
    (if (:original analysis-to-view)
      [:div.document-diff--document-evidence-list
       [:f> evidence/document-evidence-list
        {:active-highlight active-highlight
         :all-analysis all-analysis
         :highlighted doc-evidence
         :is-obsolete false
         :on-add-analysis-to-document-evidence identity
         :on-scroll-to-evidence (partial on-scroll-to-evidence
                                         set-active-highlight)
         :hide-add-buttons true
         :set-active-highlight set-active-highlight
         :set-selected-construct identity
         :title-suffix [:div.level.is-flex-grow-1.mb-2.mr-2
                        [:span (str "Evidence for " filename)]
                        (when document-2-id
                          [:button.button.is-tertiary
                           {:on-click #(set-analysis-to-view
                                        (fn [m] (update m :original not)))}
                           [:i.icon.fas.fa-caret-left]])]}]]
      [:button.button.is-tertiary
       {:on-click #(set-analysis-to-view
                    (fn [m] (update m :original not)))}
       [:i.icon.fas.fa-caret-right]])

    [:div.doc-diff--documents-panel-wrapper
     [:div.doc-diff--documents-panel

      ; First document
      [:div.doc-diff--document.mx-1
       [:div.document-diff--doc-picker
        [:b filename " - Original - Read Only"]]
       ; Document 1 PDF
       [:f> document-viewer-layout
        {:loaded loaded
         :set-pdf-ref set-pdf-ref}]]

      ; Second Document
      [:div.doc-diff--document.mx-1
       [:div.document-diff--doc-picker
        ; Document Picker
        [:f> doc-picker/document-picker
         document-2-id
         @(rf/subscribe [::documents/loading])
         (->> all-documents
              (filter (comp not :is-obsolete))
              (sort-by (comp str/lower-case :filename)))
         on-select-document-2
         true]
        ; Document 2 Attribution
        [:f> popup-menu/popup-menu
         {:trigger-content "Attribution"
          :trigger-class (when-not (and (:information-type attribution)
                                        (:provider attribution))
                           "is-info")
          ;;:modal-instance modal-idx
          :disabled (not document-2-id)
          :modal-instance modal-instance
          :suspend-click-to-close true
          :menu-class :document-viewer-right-sidebar-attribution-menu
          ;;:disabled is-obsolete
          :content [:f> attribution/attribution-editor
                    attribution-props
                    filename-2
                    set-modal-instance]}]]

       ; Document 2 PDF
       (if (and url2 attribution)
         [:f> document-viewer-layout
          {:loaded loaded2
           :set-pdf-ref set-pdf-ref2}]
         (when (and url2 (not attribution))
           [:div.notification.is-warning.mt-0.py-1.mb-2.is-flex
            [:div.m-auto
             [:h6.title.is-6.my-3 "Set Attribution to proceed"]
             "Set attribution for " filename-2]]))]]

     ; Diff content overlay - abs positioned
     [:f> differences-overlay {:active-diff active-diff
                               :active-highlight active-highlight
                               :document-2-id document-2-id
                               :doc-evidence doc-evidence
                               :filename filename
                               :filename-2 filename-2
                               :selected-text selected-text}]]
    ; Document-2 Evidence Panel
    (if (:document-2 analysis-to-view)
      [:div.document-diff--document-evidence-list
       [:f> evidence/document-evidence-list
        {:active-highlight active-highlight2
         :set-active-highlight set-active-highlight2
         :all-analysis all-analysis
         :highlighted doc-2-evidence
         :is-obsolete false
         :on-add-analysis-to-document-evidence identity
         :on-scroll-to-evidence (partial on-scroll-to-evidence
                                         set-active-highlight2)
         :hide-add-buttons true
         :set-selected-construct identity
         :title-suffix [:div.level.is-flex-grow-1.mb-2.mr-2
                        [:span (str "Evidence for " filename-2)]
                        [:button.button.is-tertiary
                         {:on-click #(set-analysis-to-view
                                      (fn [m] (update m :document-2 not)))}
                         [:i.icon.fas.fa-caret-right]]]}]]
      [:button.button.is-tertiary
       {:disabled (not document-2-id)
        :on-click #(set-analysis-to-view (fn [m] (update m :document-2 not)))}
       [:i.icon.fas.fa-caret-left]])]]))

;; ----------------------------------------------------------------------------
;;  Controller
;; ----------------------------------------------------------------------------

(defn document-compare
  "The main controller of the PDF diff engine.
   Separates state from the design layer.
   The component accepts a filename and URL from the documents list."
  [filename url org-members]
  (let [[loaded set-loaded] (react/useState false)
        [loaded2 set-loaded2] (react/useState false)

        [pdf-doc set-pdf-doc] (react/useState nil)
        [pdf-doc2 set-pdf-doc2] (react/useState nil)

        [pdf-ref set-pdf-ref] (react/useState nil)
        [pdf-ref2 set-pdf-ref2] (react/useState nil)

        [filename-2 set-filename-2] (react/useState nil)
        [document-2-id set-document-2-id] (react/useState nil)

        [url2 set-url2] (react/useState nil)

        [active-diff set-active-diff] (react/useState nil)
        [active-highlight set-active-highlight] (react/useState [])
        [active-highlight2 set-active-highlight2] (react/useState [])
        [analysis-to-view set-analysis-to-view] (react/useState {:original true
                                                                 :document-2 false})
        [selected-text set-selected-text] (react/useState nil)
        [search-query set-search-query] (react/useState nil)
        [selected-constructs set-selected-constructs] (react/useState nil)
        [selection-range set-selection-range] (react/useState nil)

        saving (r/atom false)

        set-selection-range (fn [{:keys [start end] :as range}]
                              (set-selection-range
                               (cond-> range
                                 (> start end)
                                 (merge {:start end :end start}))))

        all-documents @(rf/subscribe [::documents/data :filename :asc])
        all-analysis @(analysis.search/all-analysis-structures)

        doc-diff @(diff.db/doc-diff*)

        document-id (->> all-documents
                         (filter (comp (partial = filename) :filename))
                         first
                         :id)
        ;; We only load attribution for document 2 because we are only creating
        ;; evidence for document 2
        doc-attribution (when document-2-id
                          @(doc.db/document-attribution-data* document-2-id))
        doc-evidence @(doc.db/document-evidence-data* document-id)
        doc-2-evidence (when document-2-id
                         @(doc.db/document-evidence-data* document-2-id))
        doc-record @(doc.db/document-record* document-id)

        [attribution-state set-attribution-state] (react/useState doc-attribution)

        attribution-props {:set-field (fn [& args]
                                        (set-attribution-state #(apply assoc % args)))
                           :idx 0
                           :mode :edit
                           :organization-members org-members
                           ; favour the keyword state-evidence for the
                           ; internals of the state evidence components
                           ; attribution is a major portion of a followup
                           ; evidence entry
                           :state-evidence attribution-state
                           :validation-error nil
                           :on-save-attribution #(doc.db/set-attribution
                                                  document-2-id
                                                  attribution-state)
                           :save-person identity #_save-person}

        on-search-query (fn [q]
                          (let [construct-ids
                                (->> doc-evidence
                                     (filter (comp (partial = (:evidence-id active-highlight))
                                                   :evidence-id))
                                     first
                                     :construct-ids
                                     set)]
                            (set-selected-constructs (filterv
                                                      (comp construct-ids :construct-id)
                                                      all-analysis))
                            (set-attribution-state
                             #(assoc-in % [:reference] (doc.db/page-range->string selection-range)))
                            (set-search-query (assoc q :construct-ids construct-ids))))

        on-scroll-to (fn [id-prefix evidence-id paragraph]
                       (some-> (str id-prefix
                                    "highlight-"
                                    evidence-id
                                    "-"
                                    (-> paragraph
                                        :content-data
                                        :page-range
                                        :start))
                               (->> (.getElementById js/document))
                               .-children
                               array-seq
                               first
                               (.scrollIntoView
                                #js{:behavior "smooth"
                                    :block "center"})))

        on-scroll-to-evidence (fn [set-active-highlight evidence-id paragraph]
                                (set-active-highlight {:evidence-id evidence-id
                                                       :type :evidence})
                                (on-scroll-to "doc1-" evidence-id paragraph)
                                (on-scroll-to "doc2-" evidence-id paragraph))

        on-cancel-evidence (fn [_]
                             (on-search-query nil)
                             (set-search-query nil)
                             (set-selected-text nil))

        on-save-callback (fn [_]
                           (on-search-query nil)
                           (set-search-query nil)
                           (set-selected-text nil)
                           (reset! saving false))

        on-save-evidence (fn [_]
                           (doc.db/on-create-document-evidence-from-selection
                            document-2-id
                            (:text search-query)
                            attribution-props
                            selection-range
                            selected-constructs
                            on-save-callback))
        on-select-document-2 (fn [selected-id]
                               (let [filename
                                     (->> all-documents
                                          (filter (comp (partial = selected-id)
                                                        :id))
                                          first
                                          :filename)]
                                 (set-filename-2 filename)
                                 (set-url2 nil)
                                 (set-loaded2 false)
                                 (set-document-2-id selected-id)
                                 (doc.db/fetch-document-evidence selected-id)
                                 (when pdf-doc2 (set-pdf-doc2 nil))
                                 (when pdf-ref2 (set! (.-innerHTML pdf-ref2) ""))
                                 (doc.db/fetch-document-attribution selected-id)
                                 (diff.db/fetch-comparison-file-url filename set-url2)
                                 (diff.db/fetch-diff document-id selected-id)))]

    ;; - On load:: Fetch the original document and its evidence
    (react/useEffect
     (fn []
       (doc.db/fetch-document-record document-id)
       (doc.db/fetch-document-evidence document-id))
     #js [nil])

    (react/useEffect
     (fn []
       (doc.db/fetch-document-evidence document-id)
       (when document-2-id
        (doc.db/fetch-document-evidence document-2-id)))
     #js [all-analysis])


    ;; - Load Document Attribution :: Update local state for attribution form
    (react/useEffect
     (fn [] (set-attribution-state doc-attribution))
     #js [doc-attribution])

    ;; - When either document loads, we force the stylesheet to scale for side-by-side
    ;;   persentation of documents
    (react/useEffect
     (fn []
       ; Forces the CSS rule for PDF scale factor to 1.2
       ; This is very hacky but handles the change in scale
       ; from the doc-viewer, scaled to 1.5 versus the
       ; side-by-side presentation of the diff, scaled to 1
       (.setProperty (.-style js/document.documentElement)
                     "--scale-factor"
                     "1.0")
       (fn []
         ; Resets the CSS rule for PDF scale factor to 1.5
         (.setProperty (.-style js/document.documentElement)
                       "--scale-factor"
                       "1.5")))
     (array loaded loaded2))

    ;; When the first document loads we render the first PDF
    (react/useEffect
     (fn []
       (when (and pdf-ref (not loaded))
         (let [counter (r/atom 0)
               ;; We send the total pages to load from the
               ;; PDF reader, and count callbacks - once we
               ;; have loaded all of the pages, we set loaded
               ;; to true and process to rendering evidence
               on-load (fn [page-count]
                         (swap! counter inc)
                         (when (= @counter page-count)
                           (set-loaded true)))]
           (pdf/render-document {:url url
                                 :pdf-doc pdf-doc
                                 :pdf-ref pdf-ref
                                 :loaded loaded
                                 :set-pdf-doc set-pdf-doc
                                 :on-load on-load
                                 :id-prefix "doc1-"
                                 :width 560
                                 :scale 1.0}))))
     (array url loaded pdf-ref))

    ;; When the first document is selected we render the second PDF
    (react/useEffect
     (fn []
       (when (and pdf-ref2 (not loaded2) url2)
         (let [counter (r/atom 0)
               ;; We send the total pages to load from the
               ;; PDF reader, and count callbacks - once we
               ;; have loaded all of the pages, we set loaded
               ;; to true and process to rendering evidence
               on-load (fn [page-count]
                         (swap! counter inc)
                         (when (= @counter page-count)
                           (set-loaded2 true)))]
           (pdf/render-document {:url url2
                                 :pdf-doc pdf-doc2
                                 :pdf-ref pdf-ref2
                                 :loaded loaded2
                                 :set-pdf-doc set-pdf-doc2
                                 :on-load on-load
                                 :id-prefix "doc2-"
                                 :width 560
                                 :scale 1.0}))))
     (array url2 loaded2 pdf-ref2))

    ;; When document 2 is loaded or re-loaded we rerender the paragraphs
    ;; for both documents as the diff information for the first document
    ;; will change based on the second
    (react/useEffect
     (fn [] (when (and pdf-doc2 doc-diff loaded2)
              (pdf/clear-paragraphs)
              (pdf/render-diff-paragraphs pdf-doc
                                          (:document-1-data doc-diff)
                                          "doc1-")
              (pdf/render-diff-paragraphs pdf-doc2
                                          (:document-2-data doc-diff)
                                          "doc2-")))
     (array doc-diff loaded2))

    ;; Bind the selection and mouse action handlers to the second document
    ;; Handles the selection of text from document-2 and its conversion into
    ;; evidence
    (react/useEffect
     (fn []
       (when (and doc-diff pdf-ref2)
         (js.events/bind-event-handlers
          set-selected-text
          set-selection-range
          (fn [{:keys [document-paragraph-id]}]
            (let [paragraphs (paragraphs->map doc-diff)
                  paragraph (get paragraphs document-paragraph-id)
                  other-element (.getElementById
                                 js/document
                                 (str "paragraph-"
                                      (:doc-2-paragraph-id paragraph)
                                      "-"
                                      (dec (:doc-2-page paragraph))))]
              (->> (.querySelectorAll js/document ".document-diff--active-other-paragraph")
                   array-seq
                   (mapv (fn [elem]
                           (-> elem
                               .-classList
                               (.remove "document-diff--active-other-paragraph")))))

              (when (>= (:similarity paragraph) 0.25)
                (-> other-element
                    .-classList
                    (.add "document-diff--active-other-paragraph"))
                (-> other-element
                    (.scrollIntoView
                     #js{:behavior "smooth"
                         :block "center"})))
              (set-active-diff paragraph)))
          pdf-ref2)))
     #js [pdf-ref2 doc-diff])

    ;; When document 1 loads, we load and draw its evidence
    (react/useEffect
     (fn []
       (when (and loaded doc-evidence)
         (pdf/clear-evidence pdf-doc doc-evidence "doc1-")
         (pdf/render-evidence pdf-doc doc-evidence "doc1-")))
     (array loaded doc-evidence))

    ;; When document 2 loads, we load and draw its evidence
    (react/useEffect
     (fn []
       (when (and loaded2 doc-2-evidence)
         (pdf/clear-evidence pdf-doc2 doc-2-evidence "doc2-")
         (pdf/render-evidence pdf-doc2 doc-2-evidence "doc2-")))
     (array loaded2 doc-2-evidence))

    [:<>
     [:f> document-differences-layout
      {:active-diff active-diff
       :active-highlight  active-highlight
       :active-highlight2  active-highlight2
       :analysis-to-view analysis-to-view
       :attribution attribution-state
       :attribution-props attribution-props
       :all-analysis all-analysis
       :all-documents all-documents
       :document-2-id document-2-id
       :document-id document-id
       :doc-evidence doc-evidence
       :doc-2-evidence doc-2-evidence
       :filename filename
       :filename-2 filename-2
       :loaded loaded
       :loaded2 loaded2
       :on-cancel-evidence on-cancel-evidence
       :on-save-evidence on-save-evidence
       :on-scroll-to-evidence on-scroll-to-evidence
       :on-search-query on-search-query
       :on-select-document-2 on-select-document-2
       :saving @saving
       :search-query search-query
       :selected-constructs selected-constructs
       :selected-text selected-text
       :set-active-highlight set-active-highlight
       :set-active-highlight2 set-active-highlight2
       :set-analysis-to-view set-analysis-to-view
       :set-pdf-ref set-pdf-ref
       :set-pdf-ref2 set-pdf-ref2
       :set-selected-constructs set-selected-constructs
       :url2 url2}]]))